import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Send, Square, Copy, History, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, streamAI } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtNum } from "@/lib/format";

const QUICK = {
  pajak: [
    "Bandingkan metode gross, gross-up, dan net untuk kasus ini",
    "Apa risiko kepatuhan dan dokumen yang perlu disiapkan?",
    "Bagaimana cara efisiensi pajak yang legal untuk karyawan ini?",
  ],
  hosting: [
    "Rekomendasikan susunan paket VPS ideal untuk server kami",
    "Berapa harga jual yang kompetitif vs pasar Indonesia?",
    "Resource mana yang jadi bottleneck dan perlu di-upgrade?",
  ],
  isp: [
    "Apakah harga paket sudah sehat dibanding COGS?",
    "Berapa pelanggan minimal agar wilayah ini untung?",
    "Saran rasio kontensi dan harga bandwidth yang ideal",
  ],
};

export function AiRecommendationSheet({ open, onOpenChange, tool, getContext, calcId }) {
  const { quota, refresh } = useAuth();
  const [question, setQuestion] = useState("");
  const [output, setOutput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState("");
  const [usage, setUsage] = useState(null);
  const [model, setModel] = useState(quota?.model || "");
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("new");
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api
      .get("/ai/history", { params: { tool, limit: 10 } })
      .then(({ data }) => setHistory(data))
      .catch(() => {});
  }, [open, tool]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [output]);

  const run = async (q) => {
    if (streaming) return;
    setOutput("");
    setError("");
    setUsage(null);
    setStreaming(true);
    setTab("new");
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamAI(
        { tool, context: getContext(), question: q || null, calc_id: calcId || null },
        {
          signal: ctrl.signal,
          onStart: (ev) => setModel(ev.model),
          onDelta: (c) => setOutput((o) => o + c),
          onDone: (ev) => setUsage(ev),
          onError: (m) => setError(m),
        }
      );
    } catch (e) {
      if (e.name !== "AbortError") setError(errMsg(e, "Gagal menghubungi AI"));
    } finally {
      setStreaming(false);
      abortRef.current = null;
      refresh();
      api
        .get("/ai/history", { params: { tool, limit: 10 } })
        .then(({ data }) => setHistory(data))
        .catch(() => {});
    }
  };

  const stop = () => abortRef.current && abortRef.current.abort();
  const pct = quota?.limit ? Math.min(100, (quota.used / quota.limit) * 100) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[640px] bg-[hsl(220_14%_6%)] border-l border-[color:var(--hairline)] p-0 flex flex-col"
        data-testid="ai-recommendation-sheet"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border text-left">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <SheetTitle className="font-display text-2xl font-normal">Rekomendasi AI</SheetTitle>
            {model && (
              <Badge variant="outline" className="ml-1 border-[color:var(--hairline)] text-[hsl(43_60%_66%)] num text-[10px]" data-testid="ai-model-badge">
                {model}
              </Badge>
            )}
          </div>
          <SheetDescription className="text-xs">
            AI membaca input & hasil perhitungan di layar ini, lalu memberi analisa dan saran. Hasil AI bersifat referensi.
          </SheetDescription>
          {quota && (
            <div className="pt-2">
              <div className="flex justify-between text-[11px] text-muted-foreground">
                <span>Kuota token bulan ini</span>
                <span className="num" data-testid="ai-quota-text">
                  {fmtNum(quota.used)} / {fmtNum(quota.limit)}
                </span>
              </div>
              <Progress value={pct} className="h-1.5 mt-1 bg-[hsl(220_10%_14%)]" />
            </div>
          )}
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="mx-6 mt-3 w-fit bg-[hsl(220_12%_9%)]">
            <TabsTrigger value="new" data-testid="ai-tab-new">Rekomendasi</TabsTrigger>
            <TabsTrigger value="history" data-testid="ai-tab-history">
              <History className="h-3.5 w-3.5 mr-1.5" /> Riwayat ({history.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="new" className="flex-1 min-h-0 m-0">
            <div ref={scrollRef} className="h-full overflow-y-auto px-6 py-4 scroll-gold" data-testid="ai-output-area">
              {!output && !streaming && !error && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-border p-4 bg-card">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Tekan <b className="text-foreground">Buat Rekomendasi</b> untuk analisa umum, atau pilih / tulis pertanyaan spesifik di bawah.
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {(QUICK[tool] || []).map((q, i) => (
                      <button
                        key={q}
                        onClick={() => run(q)}
                        className="press text-left text-sm rounded-lg border border-border px-3.5 py-2.5 text-[hsl(45_15%_82%)] hover:border-[color:var(--hairline)] hover:bg-[hsl(43_30%_8%)]"
                        data-testid={`ai-quick-prompt-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {streaming && !output && (
                <div className="space-y-3" data-testid="ai-loading">
                  <Skeleton className="h-4 w-2/3 bg-[hsl(220_10%_13%)]" />
                  <Skeleton className="h-4 w-full bg-[hsl(220_10%_13%)]" />
                  <Skeleton className="h-4 w-5/6 bg-[hsl(220_10%_13%)]" />
                  <Skeleton className="h-4 w-1/2 bg-[hsl(220_10%_13%)]" />
                  <p className="text-xs text-muted-foreground pt-1">AI sedang menganalisa data Anda…</p>
                </div>
              )}
              {output && (
                <div className={`ai-markdown ${streaming ? "blink-caret" : ""}`} data-testid="ai-output-text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{output}</ReactMarkdown>
                </div>
              )}
              {error && (
                <div className="mt-3 rounded-lg border border-[hsl(0_60%_40%)] bg-[hsl(0_40%_10%)] p-3 text-sm flex gap-2" data-testid="ai-error">
                  <AlertTriangle className="h-4 w-4 text-[hsl(0_72%_62%)] mt-0.5 shrink-0" /> {error}
                </div>
              )}
              {usage && (
                <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground" data-testid="ai-recommendation-tokens-used">
                  <Badge variant="outline" className="border-border num">
                    {fmtNum(usage.usage?.total_tokens)} token dipakai
                  </Badge>
                  <span className="num">input {fmtNum(usage.usage?.input_tokens)} · output {fmtNum(usage.usage?.output_tokens)}</span>
                  {output && (
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(output);
                        toast.success("Disalin");
                      }}
                      className="ml-auto inline-flex items-center gap-1 hover:text-primary"
                      data-testid="ai-copy-button"
                    >
                      <Copy className="h-3.5 w-3.5" /> Salin
                    </button>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
          <TabsContent value="history" className="flex-1 min-h-0 m-0">
            <div className="h-full overflow-y-auto px-6 py-4 space-y-3 scroll-gold" data-testid="ai-history-list">
              {history.length === 0 && <p className="text-sm text-muted-foreground">Belum ada riwayat rekomendasi untuk tool ini.</p>}
              {history.map((h) => (
                <details key={h.id} className="rounded-lg border border-border bg-card group">
                  <summary className="cursor-pointer list-none p-3 flex items-center gap-2 text-sm">
                    <span className="flex-1 truncate">{h.question || "Rekomendasi umum"}</span>
                    <span className="text-[11px] text-muted-foreground num">{fmtDate(h.created_at)}</span>
                  </summary>
                  <div className="px-3 pb-3 ai-markdown border-t border-border pt-2">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{h.response || ""}</ReactMarkdown>
                  </div>
                </details>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="border-t border-border p-4 space-y-2.5 bg-[hsl(220_14%_5%)]">
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Tulis pertanyaan khusus (opsional)…"
            className="min-h-[64px] bg-[hsl(220_12%_8%)] border-border text-sm resize-none"
            data-testid="ai-recommendation-question-input"
            maxLength={1500}
          />
          <div className="flex gap-2">
            {streaming ? (
              <Button variant="secondary" className="flex-1" onClick={stop} data-testid="ai-stop-button">
                <Square className="h-4 w-4" /> Hentikan
              </Button>
            ) : (
              <Button
                className="flex-1 press"
                onClick={() => run(question)}
                disabled={quota && quota.used >= quota.limit}
                data-testid="ai-recommendation-send-button"
              >
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : question.trim() ? <Send className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
                {question.trim() ? "Kirim Pertanyaan" : "Buat Rekomendasi"}
              </Button>
            )}
          </div>
          {quota && quota.used >= quota.limit && (
            <p className="text-[11px] text-[hsl(0_72%_64%)]" data-testid="ai-quota-exceeded">Kuota token bulan ini sudah habis. Hubungi admin.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
